<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxId: { type: [Number, String], required: true },
  groups: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const isRunning = ref(false);
const done = ref(false);
const progress = ref(0);
const results = ref([]);

const tk = key => t(`INBOX_MGMT.GROUP_EXTRACT.${key}`);

const totals = computed(() =>
  results.value.reduce(
    (acc, r) => ({
      created: acc.created + (r.created || 0),
      existing: acc.existing + (r.existing || 0),
      skipped: acc.skipped + (r.skipped || 0),
    }),
    { created: 0, existing: 0, skipped: 0 }
  )
);

const reset = () => {
  isRunning.value = false;
  done.value = false;
  progress.value = 0;
  results.value = [];
};

watch(
  () => props.show,
  value => {
    if (value) reset();
  }
);

const run = async () => {
  isRunning.value = true;
  results.value = [];
  // Sequential: um grupo por request (não estoura timeout e não martela o WAHA).
  for (let i = 0; i < props.groups.length; i += 1) {
    const group = props.groups[i];
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await InboxesAPI.extractWhatsappGroupContacts(
        props.inboxId,
        { jid: group.jid, name: group.name }
      );
      results.value.push({ name: group.name || group.jid, ...data });
    } catch {
      results.value.push({ name: group.name || group.jid, error: true });
    }
    progress.value = Math.round(((i + 1) / props.groups.length) * 100);
  }
  isRunning.value = false;
  done.value = true;
};

const close = () => emit('close');
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      @click.self="close"
    >
      <div
        class="w-full max-w-lg overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-5">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-contact-round text-lg" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ tk('TITLE') }}
              </h3>
              <p class="truncate text-sm text-n-slate-10">
                {{ tk('SELECTED_COUNT', { count: groups.length }) }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="close"
          >
            <span class="i-lucide-x text-base" />
          </button>
        </div>

        <div class="space-y-4 px-5 pb-2">
          <!-- LGPD warning -->
          <div
            class="flex items-start gap-2.5 rounded-xl border border-n-amber-5 bg-n-amber-3 p-3"
          >
            <span
              class="i-lucide-shield-alert mt-0.5 shrink-0 text-base text-n-amber-11"
            />
            <p class="text-sm text-n-amber-12">{{ tk('LGPD_WARNING') }}</p>
          </div>

          <!-- Selected groups -->
          <div v-if="!done" class="max-h-40 overflow-y-auto">
            <ul class="flex flex-col divide-y divide-n-weak">
              <li
                v-for="group in groups"
                :key="group.jid"
                class="flex items-center gap-2 py-2 text-sm text-n-slate-12"
              >
                <span class="i-lucide-users size-4 shrink-0 text-n-slate-10" />
                {{ group.name || group.jid }}
              </li>
            </ul>
          </div>

          <!-- Progress -->
          <div v-if="isRunning" class="flex flex-col gap-1.5">
            <div class="h-1.5 w-full overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-1.5 rounded-full bg-n-teal-9 transition-all"
                :style="{ width: `${progress}%` }"
              />
            </div>
            <span class="text-xs text-n-slate-10">{{ tk('RUNNING') }}</span>
          </div>

          <!-- Results -->
          <div v-if="done" class="flex flex-col gap-3">
            <div
              class="rounded-xl border border-n-teal-5 bg-n-teal-3 p-3 text-sm text-n-teal-12"
            >
              {{
                tk('RESULT_SUMMARY', {
                  created: totals.created,
                  existing: totals.existing,
                  skipped: totals.skipped,
                })
              }}
            </div>
            <ul class="flex flex-col divide-y divide-n-weak">
              <li
                v-for="(r, idx) in results"
                :key="idx"
                class="flex items-center justify-between py-2 text-sm"
              >
                <span class="truncate text-n-slate-12">{{ r.name }}</span>
                <span v-if="r.error" class="text-n-ruby-11">
                  {{ tk('GROUP_ERROR') }}
                </span>
                <span v-else class="shrink-0 tabular-nums text-n-slate-10">
                  {{
                    tk('GROUP_RESULT', {
                      created: r.created,
                      existing: r.existing,
                    })
                  }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-2 flex items-center justify-end gap-2 p-5">
          <Button variant="faded" color="slate" @click="close">
            {{ done ? tk('DONE') : tk('CANCEL') }}
          </Button>
          <Button
            v-if="!done"
            color="teal"
            :is-loading="isRunning"
            :disabled="isRunning || !groups.length"
            @click="run"
          >
            <span class="i-lucide-download mr-1" />
            {{ tk('EXTRACT') }}
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
